import React, { useEffect, useMemo, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { PageHeader, Disclaimer } from "@/components/Common";
import { Sparkles, Save, Trash2, Gavel, ShieldCheck, RefreshCcw, Search } from "lucide-react";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, canChangeClearance } from "@/lib/auth";
import { InlineDeleteButton } from "@/components/InlineDeleteButton";

// MP9 — AI-Lite Review Notes
// Deterministic / rule-based generator (no LLM). All notes carry the mandatory disclaimer.
// AI never approves a track; human review is always required.

export default function AIReviewNotes() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [requests, setRequests] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [generatorOpen, setGeneratorOpen] = useState(false);
  const [prefill, setPrefill] = useState({ track: "", request: "", submission: "" });

  async function loadAll() {
    setLoading(true);
    try {
      const [n, t, r, s] = await Promise.all([
        api.get("/ai-review-notes"),
        api.get("/tracks"),
        api.get("/license-requests"),
        api.get("/submissions"),
      ]);
      setNotes(n.data || []);
      setTracks(t.data || []);
      setRequests(r.data || []);
      setSubmissions(s.data || []);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }

  useEffect(() => { loadAll(); }, []);

  // Auto-open generator from deep link ?generate=<trackId>&request=<id>&submission=<id>
  useEffect(() => {
    const t = searchParams.get("generate");
    const r = searchParams.get("request") || "";
    const s = searchParams.get("submission") || "";
    if (t) {
      setPrefill({ track: t, request: r, submission: s });
      setGeneratorOpen(true);
    }
  }, [searchParams]);


  const trackById = useMemo(() => Object.fromEntries(tracks.map(t => [t.id, t])), [tracks]);

  const visible = useMemo(() => {
    if (!q) return notes;
    const lc = q.toLowerCase();
    return notes.filter(n => {
      const t = trackById[n.track_id];
      return [
        n.compliance_summary, n.missing_document_summary, n.risk_summary,
        n.suggested_next_action, n.attorney_escalation_recommendation,
        n.generated_by, t?.track_title, t?.artist, t?.track_id,
      ].filter(Boolean).join(" ").toLowerCase().includes(lc);
    });
  }, [notes, q, trackById]);

  return (
    <div data-testid="ai-review-notes-page">
      <PageHeader
        title="AI Review Notes"
        subtitle="AI-lite (rule-assisted) internal compliance support · Not legal advice · Human review required"
        actions={
          <Button onClick={() => setGeneratorOpen(true)} data-testid="ai-generate-button" style={{ background: "hsl(347 72% 22%)" }}>
            <Sparkles className="w-4 h-4 mr-2" />Generate AI Review Note
          </Button>
        }
      />

      <Card className="mb-4 border-[hsl(40_48%_32%)]/30">
        <CardContent className="p-3 flex items-start gap-2 text-xs" data-testid="ai-disclaimer-banner">
          <ShieldCheck className="w-4 h-4 mt-0.5" style={{ color: "hsl(40 48% 32%)" }} />
          <div>
            This AI-assisted module is for internal compliance support only and is not legal advice.
            The generator is deterministic and rule-based — no LLM is invoked. Human review is required before external submission or reliance.
          </div>
        </CardContent>
      </Card>

      <Card className="mb-4">
        <CardContent className="p-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[260px]">
            <label className="text-xs text-muted-foreground">Search saved notes</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Track, artist, summary content, generator…"
                value={q}
                onChange={e => setQ(e.target.value)}
                className="pl-9"
                data-testid="ai-search-input"
              />
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={loadAll} data-testid="ai-refresh"><RefreshCcw className="w-4 h-4 mr-2" />Refresh</Button>
          <span className="text-xs text-muted-foreground self-end pb-2" data-testid="ai-notes-count">{visible.length} of {notes.length}</span>
        </CardContent>
      </Card>

      <div className="space-y-3" data-testid="ai-notes-list">
        {loading && <div className="text-sm text-muted-foreground py-10 text-center">Loading…</div>}
        {!loading && visible.length === 0 && (
          <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">
            No AI review notes saved yet. Use <span className="font-medium text-foreground">Generate AI Review Note</span> to draft one.
          </CardContent></Card>
        )}
        {visible.map(n => (
          <NoteCard
            key={n.ai_note_id}
            note={n}
            track={trackById[n.track_id]}
            canAct={canChangeClearance(user)}
            onChanged={loadAll}
            onOpenTrack={(id) => navigate(`/tracks/${id}`)}
          />
        ))}
      </div>

      <Disclaimer>
        Compliance support only · Not legal advice · Human review required before any external submission or reliance.
      </Disclaimer>

      <GeneratorSheet
        open={generatorOpen}
        onOpenChange={(v) => {
          setGeneratorOpen(v);
          if (!v) {
            const n = new URLSearchParams(searchParams);
            n.delete("generate"); n.delete("request"); n.delete("submission");
            setSearchParams(n, { replace: true });
            setPrefill({ track: "", request: "", submission: "" });
          }
        }}
        tracks={tracks}
        requests={requests}
        submissions={submissions}
        defaultTrackId={prefill.track}
        defaultRequestId={prefill.request}
        defaultSubmissionId={prefill.submission}
        onSaved={() => { setGeneratorOpen(false); loadAll(); }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Generator sheet
// ---------------------------------------------------------------------------
export function GeneratorSheet({ open, onOpenChange, tracks, requests, submissions, onSaved, defaultTrackId, defaultRequestId, defaultSubmissionId }) {
  const [trackId, setTrackId] = useState(defaultTrackId || "");
  const [requestId, setRequestId] = useState(defaultRequestId || "");
  const [submissionId, setSubmissionId] = useState(defaultSubmissionId || "");
  const [draft, setDraft] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTrackId(defaultTrackId || "");
      setRequestId(defaultRequestId || "");
      setSubmissionId(defaultSubmissionId || "");
      setDraft(null);
    }
  }, [open, defaultTrackId, defaultRequestId, defaultSubmissionId]);

  async function generate() {
    if (!trackId) { toast.error("Pick a track first"); return; }
    setGenerating(true);
    try {
      const r = await api.post("/ai-review-notes/generate", {
        track_id: trackId,
        request_id: requestId || "",
        submission_id: submissionId || "",
      });
      setDraft(r.data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setGenerating(false); }
  }

  async function save() {
    if (!draft) return;
    setSaving(true);
    try {
      await api.post("/ai-review-notes", {
        track_id: draft.track_id,
        request_id: draft.request_id || "",
        submission_id: draft.submission_id || "",
        compliance_summary: draft.compliance_summary,
        missing_document_summary: draft.missing_document_summary,
        risk_summary: draft.risk_summary,
        suggested_next_action: draft.suggested_next_action,
        sync_readiness_note: draft.sync_readiness_note,
        attorney_escalation_recommendation: draft.attorney_escalation_recommendation,
        human_review_required: true,
        disclaimer_text: draft.disclaimer_text,
      });
      toast.success("AI-Lite review note saved");
      onSaved && onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  }

  function set(k, v) { setDraft(d => ({ ...d, [k]: v })); }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="!max-w-[640px] w-full overflow-y-auto" data-testid="ai-generator-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4" />Generate AI-Lite Review Note</SheetTitle>
        </SheetHeader>

        <div className="space-y-3 mt-3">
          <div>
            <Label className="text-xs">Track</Label>
            <Select value={trackId || "unset"} onValueChange={v => setTrackId(v === "unset" ? "" : v)}>
              <SelectTrigger data-testid="ai-track-select"><SelectValue placeholder="Pick a track…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unset">—</SelectItem>
                {(tracks || []).map(t => <SelectItem key={t.id} value={t.id}>{t.track_title} — {t.artist} ({t.clearance_status})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">License request (optional)</Label>
              <Select value={requestId || "unset"} onValueChange={v => setRequestId(v === "unset" ? "" : v)}>
                <SelectTrigger data-testid="ai-request-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unset">—</SelectItem>
                  {(requests || []).map(r => <SelectItem key={r.id} value={r.id}>{r.request_id} — {r.project_title || "(untitled)"}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Submission (optional)</Label>
              <Select value={submissionId || "unset"} onValueChange={v => setSubmissionId(v === "unset" ? "" : v)}>
                <SelectTrigger data-testid="ai-submission-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unset">—</SelectItem>
                  {(submissions || []).map(s => <SelectItem key={s.id} value={s.id}>{s.submission_id} — {s.project_title || "(untitled)"}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={generate} disabled={!trackId || generating} className="w-full" data-testid="ai-generate-now" style={{ background: "hsl(347 72% 22%)" }}>
            <Sparkles className="w-4 h-4 mr-2" />{generating ? "Generating…" : "Generate"}
          </Button>
        </div>

        {draft && (
          <div className="mt-4 space-y-3" data-testid="ai-draft-preview">
            <DraftField label="1 · Compliance summary" value={draft.compliance_summary} onChange={v => set("compliance_summary", v)} testid="ai-draft-compliance" />
            <DraftField label="2 · Missing documents / data gaps" value={draft.missing_document_summary} onChange={v => set("missing_document_summary", v)} testid="ai-draft-missing" />
            <DraftField label="3 · Risk flags" value={draft.risk_summary} onChange={v => set("risk_summary", v)} testid="ai-draft-risk" />
            <DraftField label="4 · Suggested next action" value={draft.suggested_next_action} onChange={v => set("suggested_next_action", v)} testid="ai-draft-next" />
            <DraftField label="5 · Attorney review recommendation" value={draft.attorney_escalation_recommendation} onChange={v => set("attorney_escalation_recommendation", v)} testid="ai-draft-attorney" />
            <DraftField label="Sync readiness note" value={draft.sync_readiness_note} onChange={v => set("sync_readiness_note", v)} testid="ai-draft-sync" />
            {Array.isArray(draft.human_review_checklist) && draft.human_review_checklist.length > 0 && (
              <div data-testid="ai-draft-checklist">
                <Label className="text-xs">6 · Human review checklist</Label>
                <ul className="list-disc list-inside text-xs text-muted-foreground bg-secondary/40 rounded p-3 mt-1">
                  {draft.human_review_checklist.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </div>
            )}
            <Card className="border-[hsl(40_48%_32%)]/30">
              <CardContent className="p-3 flex items-start gap-2 text-[11px]" data-testid="ai-draft-disclaimer">
                <ShieldCheck className="w-4 h-4 mt-0.5" style={{ color: "hsl(40 48% 32%)" }} />
                <div>{draft.disclaimer_text}</div>
              </CardContent>
            </Card>

            <div className="flex gap-2">
              <Button onClick={save} disabled={saving} data-testid="ai-save-note" style={{ background: "hsl(347 72% 22%)" }} className="flex-1">
                <Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save Note"}
              </Button>
              <Button variant="outline" onClick={() => setDraft(null)} data-testid="ai-discard-note">
                <Trash2 className="w-4 h-4 mr-2" />Discard
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function DraftField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Textarea value={value || ""} onChange={e => onChange(e.target.value)} rows={3} data-testid={testid} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Saved note card
// ---------------------------------------------------------------------------
function NoteCard({ note, track, canAct, onChanged, onOpenTrack }) {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note);
  const [saving, setSaving] = useState(false);

  function set(k, v) { setDraft(d => ({ ...d, [k]: v })); }

  async function saveEdit() {
    setSaving(true);
    try {
      await api.put(`/ai-review-notes/${note.ai_note_id}`, {
        track_id: draft.track_id,
        request_id: draft.request_id || "",
        submission_id: draft.submission_id || "",
        compliance_summary: draft.compliance_summary || "",
        missing_document_summary: draft.missing_document_summary || "",
        risk_summary: draft.risk_summary || "",
        suggested_next_action: draft.suggested_next_action || "",
        sync_readiness_note: draft.sync_readiness_note || "",
        attorney_escalation_recommendation: draft.attorney_escalation_recommendation || "",
        human_review_required: true,
        disclaimer_text: draft.disclaimer_text || "",
      });
      toast.success("Saved");
      setEditing(false);
      onChanged && onChanged();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  }

  async function remove() {
    if (!window.confirm("Discard this AI review note? This cannot be undone.")) return;
    try {
      await api.delete(`/ai-review-notes/${note.ai_note_id}`);
      toast.success("Discarded");
      onChanged && onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  }

  async function flagAttorney() {
    if (!track || !canAct) return;
    if (!window.confirm("Set the linked track's status to 'Attorney Review Required'?")) return;
    try {
      await api.post(`/tracks/${track.id}/attorney-review`, {
        attorney_review_required: true,
        attorney_review_reason: "Flagged from AI-Lite review note",
      });
      toast.success("Track flagged for Attorney Review");
      onChanged && onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  }

  return (
    <Card data-testid={`ai-note-${note.ai_note_id}`}>
      <CardContent className="p-5">
        <div className="flex items-start gap-3 flex-wrap mb-3">
          <Sparkles className="w-4 h-4 mt-1" style={{ color: "hsl(40 48% 32%)" }} />
          <div className="min-w-0 flex-1">
            <div className="font-display text-base truncate">
              {track ? `${track.track_title} — ${track.artist}` : <span className="text-muted-foreground">Track {note.track_id?.slice(0, 8) || "—"}</span>}
            </div>
            <div className="text-[11px] text-muted-foreground font-mono">{note.generated_at?.slice(0, 19).replace("T", " ")} · {note.generated_by}</div>
          </div>
          <div className="flex gap-2">
            {track && (
              <Button size="sm" variant="ghost" onClick={() => onOpenTrack(track.id)} data-testid={`ai-note-open-${note.ai_note_id}`}>Open track</Button>
            )}
            {!editing ? (
              <Button size="sm" variant="outline" onClick={() => { setDraft(note); setEditing(true); }} data-testid={`ai-note-edit-${note.ai_note_id}`}>Edit</Button>
            ) : (
              <>
                <Button size="sm" onClick={saveEdit} disabled={saving} data-testid={`ai-note-save-${note.ai_note_id}`}><Save className="w-4 h-4 mr-1" />Save</Button>
                <Button size="sm" variant="outline" onClick={() => { setEditing(false); setDraft(note); }} data-testid={`ai-note-cancel-${note.ai_note_id}`}>Cancel</Button>
              </>
            )}
            <InlineDeleteButton
              endpoint={`/ai-review-notes/${note.ai_note_id}`}
              recordLabel="AI review note"
              recordId={note.ai_note_id}
              isSuperAdmin={user?.role === "super_admin"}
              variant="outline"
              onDeleted={() => { onChanged && onChanged(); }}
              testid={`ai-note-discard-${note.ai_note_id}`}
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3 text-sm">
          <Section title="1 · Compliance summary" value={editing ? null : note.compliance_summary} editValue={draft.compliance_summary} onChange={v => set("compliance_summary", v)} editing={editing} testid={`ai-note-compliance-${note.ai_note_id}`} />
          <Section title="2 · Missing documents / data gaps" value={editing ? null : note.missing_document_summary} editValue={draft.missing_document_summary} onChange={v => set("missing_document_summary", v)} editing={editing} testid={`ai-note-missing-${note.ai_note_id}`} />
          <Section title="3 · Risk flags" value={editing ? null : note.risk_summary} editValue={draft.risk_summary} onChange={v => set("risk_summary", v)} editing={editing} testid={`ai-note-risk-${note.ai_note_id}`} />
          <Section title="4 · Suggested next action" value={editing ? null : note.suggested_next_action} editValue={draft.suggested_next_action} onChange={v => set("suggested_next_action", v)} editing={editing} testid={`ai-note-next-${note.ai_note_id}`} />
          <Section title="5 · Attorney review recommendation" value={editing ? null : note.attorney_escalation_recommendation} editValue={draft.attorney_escalation_recommendation} onChange={v => set("attorney_escalation_recommendation", v)} editing={editing} testid={`ai-note-attorney-${note.ai_note_id}`} full />
          <Section title="Sync readiness note" value={editing ? null : note.sync_readiness_note} editValue={draft.sync_readiness_note} onChange={v => set("sync_readiness_note", v)} editing={editing} testid={`ai-note-sync-${note.ai_note_id}`} full />
        </div>

        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <span className="text-[11px] px-2 py-0.5 rounded status-incomplete" data-testid={`ai-note-human-required-${note.ai_note_id}`}>Human review required</span>
          {canAct && track && (
            <Button size="sm" variant="outline" onClick={flagAttorney} data-testid={`ai-note-route-attorney-${note.ai_note_id}`} style={{ borderColor: "hsl(0 70% 42%)", color: "hsl(0 70% 42%)" }}>
              <Gavel className="w-4 h-4 mr-1" />Route to Attorney Review Required
            </Button>
          )}
        </div>

        <div className="mt-3 text-[11px] text-muted-foreground bg-secondary/40 rounded p-2" data-testid={`ai-note-disclaimer-${note.ai_note_id}`}>
          {note.disclaimer_text}
        </div>
      </CardContent>
    </Card>
  );
}

function Section({ title, value, editValue, onChange, editing, testid, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{title}</div>
      {editing ? (
        <Textarea rows={3} value={editValue || ""} onChange={e => onChange(e.target.value)} data-testid={`${testid}-edit`} />
      ) : (
        <div className="text-sm" data-testid={testid}>{value || <span className="text-muted-foreground">—</span>}</div>
      )}
    </div>
  );
}
